import * as React from 'react'
import clx from 'classnames'
import { CompanyRole, CompanyRoleScope, pickPermissions } from '@/lib/api/company/roles'
import { Button } from '@/ui/button'
import { FaIcon, faCopySolid, faLock, faPencil, faPlus, faTrash } from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FieldsetInput } from '@/modules/settings/components/FieldsetInput'
import {
  countPermissions,
  isDriverRole,
  lockReason,
  roleDescription,
  roleUserCount,
  roleUsersText,
  rolesKey,
  scopeHint,
  scopeLabel,
  TOTAL_PERMISSIONS,
} from './catalog'
import { PermissionTable, ScopeBadge } from './components'
import { RoleEditor } from './useRoleEditor'

const SECTIONS: CompanyRoleScope[] = ['system', 'shared', 'serviceAccount', 'parent', 'company']

const Divider = () => <div className="tw-h-5 tw-w-0.5 tw-bg-secondary" />

/** Akcja paska jak „Dodaj kierowcę”: ikona + tekst w kolorze primary. */
const BarAction: React.FC<{
  icon: IconDefinition
  label: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}> = ({ icon, label, onClick, danger, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={clx(
      'tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 tw-text-sm tw-font-medium hover:tw-bg-base-300 hover:tw-opacity-90 disabled:tw-pointer-events-none disabled:tw-opacity-50',
      danger ? 'tw-text-error' : 'tw-text-primary',
    )}
  >
    <FaIcon icon={icon} className="tw-h-4 tw-w-4" />
    {label}
  </button>
)

// Układ A: lista ról po lewej (jak lista kierowców), tabela uprawnień po prawej.
export const RolesListLayout: React.FC<{ editor: RoleEditor; companyId: string }> = ({ editor, companyId }) => {
  const { t } = useLocalize()
  const { roles, selected, editing, draft } = editor
  const isNew = editing === 'new'
  const current = isNew ? null : selected
  const isEditing = editing !== null && (isNew || editing === current?.id)

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1">
      <aside className="tw-flex tw-w-[300px] tw-flex-shrink-0 tw-flex-col tw-border-r tw-border-base-300">
        <ul className="custom-scrollbar tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
          {SECTIONS.map((scope) => {
            const list = roles.filter((r) => r.scope === scope)
            if (list.length === 0) return null
            return (
              <React.Fragment key={scope}>
                <li className="tw-sticky tw-top-0 tw-z-10 tw-border-b tw-border-base-200 tw-bg-content-primary tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-uppercase tw-text-content-base tw-opacity-80">
                  {scopeLabel(t, scope)} ({list.length})
                </li>
                {list.map((role) => (
                  <li
                    key={role.id}
                    data-testid={`role-item-${role.id}`}
                    onClick={() => editor.select(role.id)}
                    className={clx(
                      'tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors hover:tw-bg-primary/10',
                      !isNew && current?.id === role.id && 'tw-bg-primary/20 tw-font-medium',
                    )}
                  >
                    <div className="tw-min-w-0 tw-flex-1">
                      <div className="tw-truncate tw-text-content-base" title={role.name}>
                        {role.name}
                      </div>
                      <div className="tw-truncate tw-text-xs tw-font-normal tw-text-content-base tw-opacity-60">
                        {isDriverRole(role)
                          ? scopeLabel(t, role.scope)
                          : t(rolesKey('permissionsCount'), { count: countPermissions(role), total: TOTAL_PERMISSIONS })}
                      </div>
                    </div>
                    <LockIcon reason={lockReason(t, role, companyId, editor.canManage)} />
                    <span
                      className="tw-flex-shrink-0 tw-text-xs tw-font-normal tw-text-content-base tw-opacity-70"
                      title={roleUsersText(t, role)}
                    >
                      {roleUserCount(role)}
                    </span>
                  </li>
                ))}
              </React.Fragment>
            )
          })}
          {isNew && (
            <li className="tw-border-b tw-border-base-300/50 tw-bg-primary/20 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base">
              {draft?.name || t(rolesKey('newRole'))}
            </li>
          )}
        </ul>
      </aside>

      <section className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
        <div className="tw-flex tw-min-h-[52px] tw-flex-shrink-0 tw-flex-wrap tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-1.5">
          {isEditing ? (
            <p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-content-base">
              {isNew ? t(rolesKey('newRole')) : t(rolesKey('editTitle'), { name: current?.name ?? '' })}
            </p>
          ) : (
            <>
              {editor.canManage && (
                <BarAction icon={faPlus} label={t(rolesKey('newRole'))} onClick={() => editor.startNew()} />
              )}
              {current && editor.canManage && (
                <>
                  <Divider />
                  {current.canEdit && (
                    <BarAction icon={faPencil} label={t(rolesKey('edit'))} onClick={() => editor.startEdit(current)} />
                  )}
                  {!isDriverRole(current) && (
                    <BarAction icon={faCopySolid} label={t(rolesKey('duplicate'))} onClick={() => editor.startNew(current)} />
                  )}
                  {current.canEdit && (
                    <BarAction icon={faTrash} label={t(rolesKey('delete'))} danger onClick={() => editor.removeRole(current)} />
                  )}
                </>
              )}
              {!editor.canManage && (
                <p className="tw-mb-0 tw-text-xs tw-text-content-base tw-opacity-70">{t(rolesKey('readOnly'))}</p>
              )}
            </>
          )}
        </div>

        <div className="custom-scrollbar tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-4">
          {isEditing && draft ? (
            <RoleForm editor={editor} />
          ) : current ? (
            <RoleDetails editor={editor} role={current} companyId={companyId} />
          ) : null}
        </div>

        {isEditing && draft && (
          <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-base-300 tw-px-4 tw-py-3">
            {current && (current.usersAffected ?? 0) > 0 && (
              <p className="tw-mb-0 tw-mr-auto tw-text-xs tw-text-content-base tw-opacity-80">
                {t(rolesKey('affectedWarning'), { count: current.usersAffected })}
              </p>
            )}
            <Button
              size="sm"
              variant="secondary"
              className="tw-rounded-lg tw-px-4 tw-text-content-secondary"
              text={t(rolesKey('cancel'))}
              onClick={() => editor.cancel()}
            />
            <Button
              size="sm"
              className="tw-rounded-lg tw-px-4"
              text={t(rolesKey('save'))}
              loading={editor.saving}
              disabled={!editor.isDirty}
              onClick={() => editor.submit()}
            />
          </div>
        )}
      </section>
    </div>
  )
}

const RoleDetails: React.FC<{ editor: RoleEditor; role: CompanyRole; companyId: string }> = ({
  editor,
  role,
  companyId,
}) => {
  const { t } = useLocalize()
  const description = roleDescription(t, role)
  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <header>
        <div className="tw-flex tw-items-center tw-gap-2">
          <h2 className="tw-mb-0 tw-truncate tw-text-lg tw-font-semibold tw-text-content-base">{role.name}</h2>
          <ScopeBadge role={role} />
          <LockIcon reason={lockReason(t, role, companyId, editor.canManage)} />
        </div>
        <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-base tw-opacity-60">
          {scopeHint(t, role, role.ownerCompanyId === companyId)} · {roleUsersText(t, role)}
        </p>
        {description && <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-content-base">{description}</p>}
      </header>
      {editor.error && <ErrorBox text={editor.error} />}
      {!isDriverRole(role) && <PermissionTable value={pickPermissions(role)} editable={false} />}
      <p className="tw-mb-0 tw-text-xs tw-text-content-base tw-opacity-60">{t(rolesKey('intro'))}</p>
    </div>
  )
}

const RoleForm: React.FC<{ editor: RoleEditor }> = ({ editor }) => {
  const { t } = useLocalize()
  const draft = editor.draft!
  return (
    <form
      className="tw-flex tw-flex-col tw-gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        editor.submit()
      }}
    >
      <div className="tw-grid tw-max-w-4xl tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-[1fr_2fr]">
        <FieldsetInput
          label={t(rolesKey('name'))}
          required
          value={draft.name}
          placeholder={t(rolesKey('name'))}
          onChange={(e) => editor.setField('name', e.target.value.slice(0, 100))}
        />
        <FieldsetInput
          label={t(rolesKey('description'))}
          value={draft.description ?? ''}
          placeholder={t(rolesKey('descriptionPlaceholder'))}
          onChange={(e) => editor.setField('description', e.target.value.slice(0, 500))}
        />
      </div>
      {editor.error && <ErrorBox text={editor.error} />}
      <PermissionTable value={draft} editable onToggle={editor.toggle} onToggleGroup={editor.setGroup} />
    </form>
  )
}

/** Kłódka przy roli tylko do odczytu; dymek mówi dlaczego. */
const LockIcon: React.FC<{ reason: string | null }> = ({ reason }) =>
  reason ? (
    <Tooltip text={reason} portal zIndex={9999} maxWidth="280px">
      <FaIcon icon={faLock} className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-content-base tw-opacity-50" aria-label={reason} />
    </Tooltip>
  ) : null

export const ErrorBox: React.FC<{ text: string }> = ({ text }) => (
  <p role="alert" className="tw-mb-0 tw-rounded-md tw-bg-error/10 tw-px-3 tw-py-2 tw-text-sm tw-text-error">
    {text}
  </p>
)
